import React, { useEffect, useRef, useState } from "react"
import MatchCard, { matchesSearch } from "./MatchCard"
import DrawConnections from "./DrawConnections"
import GroupList from "./GroupList"
import useDragPan from "../useDragPan"
import roundLabel, { roundsToFinal } from "../../../roundLabel"

export const roundName = (round, index, rounds) =>
  roundLabel(roundsToFinal(round?.[0]?.competition) ?? rounds.length - index - 1)
export const drawSections = (mainDraw, drawType) => {
  const rounds =
    (drawType === "groups"
      ? mainDraw.matches
      : mainDraw.phases?.length
      ? mainDraw.phases
      : mainDraw.matches) || []
  return [
    ...(mainDraw.qualifications?.length &&
    drawType !== "groups" &&
    !mainDraw.phases?.length
      ? [
          {
            label: "Qualifications",
            rows: mainDraw.qualifications,
            qualification: true,
          },
        ]
      : []),
    ...rounds.map((round, index) => ({
      label:
        drawType === "groups"
          ? `Group ${String.fromCharCode(65 + index)}`
          : roundName(round, index, rounds),
      rows: round,
      group: mainDraw.groups?.[index],
    })),
  ]
}
// As on the Lucky Draw, every qualifying match takes the bracket row of the first-round match its
// winner joins (the same rule DrawConnections draws); qualifying matches feeding the same first-round
// match share its row, and one feeding no listed match gets a row of its own after the last match.
// Rows are local to the round's match list, so the first match is row 1.
export const qualificationRows = sections => {
  const qualification = sections.findIndex(section => section.qualification)
  const firstRound = sections.findIndex(section => !section.qualification)
  if (qualification < 0 || firstRound < 0) return null
  const first = sections[firstRound].rows
  const cells = new Map()
  let count = first.length
  sections[qualification].rows.forEach((row, index) => {
    const destination = Math.ceil(Number(row.competition?.nextStagePosition) / 2)
    const target = first.findIndex(match => Number(match.competition?.position) === destination)
    const cell = target >= 0 ? target : count++
    cells.set(cell, [...(cells.get(cell) || []), index])
  })
  return {
    count,
    firstRound,
    cells: [...cells].sort((a, b) => a[0] - b[0]).map(([cell, indices]) => ({ row: cell + 1, indices })),
  }
}
export const ZOOM_LEVELS = [0.5, 0.6, 0.7, 0.8, 0.9, 1]
const zoomStep = (zoom, direction) => {
  const index = ZOOM_LEVELS.indexOf(zoom)
  return ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, Math.max(0, index + direction))]
}
export default function DrawSection({
  mainDraw = {},
  matches = [],
  competitionDetails,
  eventDetails,
  setModal,
  drawType,
  view,
  query = "",
  revealedMatchIds = [],
  toolbar = null,
}) {
  const canvas = useRef(null)
  const viewport = useRef(null)
  const sections = drawSections(mainDraw, drawType)
  const bracket = view !== "list" && drawType === "eliminatory"
  const aligned = bracket ? qualificationRows(sections) : null
  const [zoom, setZoom] = useState(1)
  useDragPan(viewport, bracket)
  // Ctrl/⌘ + wheel (and a trackpad pinch, which browsers deliver the same way)
  // zooms the bracket instead of the whole page.
  useEffect(() => {
    const node = viewport.current
    if (!node || !bracket) return undefined
    const wheel = event => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      setZoom(current => zoomStep(current, event.deltaY < 0 ? 1 : -1))
    }
    node.addEventListener("wheel", wheel, { passive: false })
    return () => node.removeEventListener("wheel", wheel)
  }, [bracket])
  const jumpTo = index => {
    const section = canvas.current?.querySelectorAll(":scope > section")[index]
    const target = section?.querySelector("article") || section
    target?.scrollIntoView?.({
      block: "center",
      inline: "center",
      behavior: "smooth",
    })
    target?.focus({ preventScroll: true })
  }
  const nav = (
    <nav
      className="main-draw-round-nav"
      aria-label={drawType === "groups" ? "Groups" : "Rounds"}
    >
      {sections.map(({ label }, index) => (
        <button
          key={index}
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => jumpTo(index)}
        >
          {label}
        </button>
      ))}
    </nav>
  )
  const zoomControls = bracket && (
    <div className="main-draw-zoom" role="group" aria-label="Bracket zoom">
      <button
        type="button"
        className="btn btn-sm btn-light"
        aria-label="Zoom out"
        disabled={zoom === ZOOM_LEVELS[0]}
        onClick={() => setZoom(zoomStep(zoom, -1))}
      >
        −
      </button>
      <button
        type="button"
        className="btn btn-sm btn-light main-draw-zoom-level"
        aria-label={`Bracket zoom ${Math.round(zoom * 100)}%, reset`}
        onClick={() => setZoom(1)}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        className="btn btn-sm btn-light"
        aria-label="Zoom in"
        disabled={zoom === 1}
        onClick={() => setZoom(zoomStep(zoom, 1))}
      >
        +
      </button>
    </div>
  )
  return (
    <>
      {toolbar ? (
        <div
          className="main-draw-toolbar main-draw-toolbar--pinned"
          role="group"
          aria-label="Main Draw view"
        >
          {toolbar.search}
          <span className="main-draw-toolbar-divider" aria-hidden="true" />
          {nav}
          {zoomControls}
          <div className="main-draw-toolbar-actions">{toolbar.actions}</div>
        </div>
      ) : (
        <div className="main-draw-toolbar">
          {nav}
          {zoomControls}
        </div>
      )}
      {toolbar?.results}
      <div
        ref={viewport}
        className={`main-draw-viewport ${bracket ? "main-draw-viewport--bracket" : ""}`}
      >
        <div
          ref={canvas}
          className={`main-draw-sections main-draw-sections--${view}${
            aligned ? " main-draw-sections--aligned" : ""
          }`}
          style={
            bracket && (zoom !== 1 || aligned)
              ? {
                  ...(zoom !== 1 ? { zoom } : {}),
                  ...(aligned ? { "--main-draw-rows": aligned.count } : {}),
                }
              : undefined
          }
          data-zoom={bracket ? zoom : undefined}
        >
          {sections.map(({ label, rows, group, qualification }, index) => {
            const firstRound = aligned !== null && index === aligned.firstRound
            const position = (row, i, style) => (
                  <div
                    key={row._id || i}
                    className="main-draw-match-position"
                    data-bracket-position={`${index}:${i}`}
                    style={style}
                  >
                    <MatchCard
                      highlighted={matchesSearch(row, query) || revealedMatchIds.includes(String(row._id))}
                      qualifyingMatches={
                        drawType === "eliminatory" && !mainDraw.phases?.length
                          ? mainDraw.qualifications
                          : []
                      }
                      {...{
                        row,
                        label,
                        index: i,
                        competitionDetails,
                        eventDetails,
                        setModal,
                        matches,
                        qualification,
                      }}
                    />
                  </div>
            )
            return (
            <section
              key={index}
              aria-label={label}
              tabIndex={-1}
              className={
                qualification
                  ? "main-draw-qualifications"
                  : firstRound
                  ? "main-draw-first-round"
                  : undefined
              }
            >
              <h3>{label}</h3>
              {drawType === "groups" && (
                <GroupList group={group || []} competition={competitionDetails} />
              )}
              <div className="main-draw-round-matches">
                {aligned && qualification
                  ? aligned.cells.map(({ row, indices }) => (
                      <div
                        key={row}
                        className="main-draw-match-cell"
                        style={{ gridRow: row }}
                      >
                        {indices.map(i => position(rows[i], i))}
                      </div>
                    ))
                  : rows.map((row, i) =>
                      position(row, i, firstRound ? { gridRow: i + 1 } : undefined)
                    )}
              </div>
            </section>
            )
          })}
          {bracket && <DrawConnections canvas={canvas} sections={sections} zoom={zoom} />}
        </div>
      </div>
    </>
  )
}
